/*
 * THESIS: a speaker page as a setlist, each talk a row with its audience and
 *   length as chips, refusing the headshot-hero speaker template.
 * OWN-WORLD: Maestro grammar in cyan; talk rows read like board items with
 *   duration chips; the stage record is a plain ledger.
 * STORY: an organizer scans three talks, sees the record, opens a card in
 *   the speaking lane.
 * FIRST VIEWPORT: kicker, thesis headline, the setlist starting.
 * FORM: setlist ledger + record list; pinned direction.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getCtaContent } from '../../content/ctaContent'
import { getSpeakingContent } from '../../content/speakingContent'

function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-visible')
      return undefined
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible')
          obs.disconnect()
        }
      },
      { threshold }
    )
    obs.observe(el)
    // failsafe: if the observer never fires (hidden tab, odd engine states),
    // the section still reveals instead of holding opacity 0 forever
    const failsafe = setTimeout(() => { el.classList.add('is-visible'); obs.disconnect() }, 2600)
    return () => { obs.disconnect(); clearTimeout(failsafe) }
  }, [threshold])
  return ref
}

const SpeakingPage = () => {
  const { i18n } = useTranslation()
  const c = getSpeakingContent(i18n.language)
  const bookLabel = getCtaContent(i18n.language).close.book
  const heroRef = useReveal(0.05)
  const listRef = useReveal(0.08)

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/speaking" />

      <header className="px-6 pb-8 pt-32 md:px-16 md:pt-40">
        <div ref={heroRef} className="reveal-on-scroll mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-19/80">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
        </div>
      </header>

      <main className="px-6 pb-24 md:px-16">
        <div ref={listRef} className="reveal-on-scroll mx-auto max-w-6xl space-y-14">
          <section>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.talks.kicker}</p>
            <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
              {c.talks.items.map((t, i) => (
                <article key={t.name} className={`px-6 py-6 md:px-8 ${i % 2 ? 'bg-[#151b23]' : 'bg-[#12171e]'}`}>
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="max-w-2xl">
                      <h2 className="text-lg font-semibold text-white md:text-xl">{t.name}</h2>
                      <p className="mt-2 text-sm leading-7 text-slate-400">{t.line}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2 md:flex-col md:items-end">
                      <span className="inline-flex items-center gap-2 rounded-md border border-pyramid-19/40 bg-pyramid-19/10 px-2.5 py-1 text-[12px] font-semibold text-pyramid-19">
                        {t.length}
                      </span>
                      <span className="inline-flex items-center rounded-md border border-white/10 bg-[#1a212a] px-2.5 py-1 text-[12px] text-slate-300">
                        {t.audience}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.proof.kicker}</p>
            <ul className="mt-4 space-y-2.5">
              {c.proof.items.map((item) => (
                <li key={item} className="flex items-start gap-3 leading-7 text-slate-300">
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pyramid-19" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="text-center">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="/talk?i=speaking"
                onClick={() => trackEvent('speaking_talk_click')}
                className="inline-flex items-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-8 py-4 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5"
              >
                {c.cta.label}
                <span aria-hidden="true">&#8594;</span>
              </a>
            <a
              href="/book"
              onClick={() => trackEvent('speaking_book_click')}
              className="inline-flex items-center gap-2 rounded-[10px] border border-pyramid-19/50 px-7 py-3 font-semibold text-pyramid-19 transition-all hover:bg-pyramid-19/10"
            >
              {bookLabel}
              <span aria-hidden="true">&#8594;</span>
            </a>
            </div>
            <p className="mt-3 text-sm text-slate-500">{c.cta.sub}</p>
          </section>
        </div>
      </main>
    </div>
  )
}

export default SpeakingPage
